import { ApiError, apiErrorFrom } from './problem';
import { parseProfile } from './types';

import type { AccountProfile, DeletionResult, ExportFile, NameChanges } from './types';

/** The account API (`/api/me*`, same origin), with the session's access token on every call. */
export interface AccountApi {
  getProfile(): Promise<AccountProfile>;
  updateNames(changes: NameChanges): Promise<AccountProfile>;
  exportData(): Promise<ExportFile>;
  signOutEverywhere(): Promise<void>;
  /** Sends the typed confirmation; the API also requires a recent sign-in. */
  deleteAccount(): Promise<DeletionResult>;
}

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

export interface AccountApiOptions {
  getAccessToken: () => Promise<string | null>;
  /** Empty (the default) in the browser: the site and the API share an origin. */
  baseUrl?: string;
  fetchImpl?: FetchLike;
}

const FALLBACK_EXPORT_NAME = 'direwolf-ai-account.json';

/** The file name from `Content-Disposition: attachment; filename="…"`, kept to safe characters. */
export function exportFilename(contentDisposition: string | null): string {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition ?? '');
  const name = match?.[1]?.trim() ?? '';
  return /^[\w.-]{1,100}$/.test(name) ? name : FALLBACK_EXPORT_NAME;
}

function invalidResponse(status: number): ApiError {
  return new ApiError({ status, code: 'invalid_response', title: 'Unexpected response' });
}

const browserFetch: FetchLike = (url, init) => fetch(url, init);

export function createAccountApi(options: AccountApiOptions): AccountApi {
  const baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? browserFetch;

  async function send(method: string, path: string, body?: unknown): Promise<Response> {
    const token = await options.getAccessToken();
    if (token === null) {
      throw new ApiError({ status: 401, code: 'signed_out', title: 'Unauthorized' });
    }
    const headers = new Headers({ accept: 'application/json', authorization: `Bearer ${token}` });
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers.set('content-type', 'application/json');
      init.body = JSON.stringify(body);
    }
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, init);
    } catch {
      throw new ApiError({ status: 0, code: 'network_error', title: 'Network error' });
    }
    if (!response.ok) throw await apiErrorFrom(response);
    return response;
  }

  async function json(response: Response): Promise<unknown> {
    try {
      return (await response.json()) as unknown;
    } catch {
      throw invalidResponse(response.status);
    }
  }

  async function profile(response: Response): Promise<AccountProfile> {
    const parsed = parseProfile(await json(response));
    if (parsed === null) throw invalidResponse(response.status);
    return parsed;
  }

  return {
    getProfile: async () => profile(await send('GET', '/api/me')),
    updateNames: async (changes) => profile(await send('PATCH', '/api/me', changes)),
    exportData: async () => {
      const response = await send('GET', '/api/me/export');
      return {
        blob: await response.blob(),
        filename: exportFilename(response.headers.get('content-disposition')),
      };
    },
    signOutEverywhere: async () => {
      await send('POST', '/api/me/sign-out-everywhere');
    },
    deleteAccount: async () => {
      const response = await send('DELETE', '/api/me', { confirm: 'DELETE' });
      // 202: accepted; the API finishes the deletion shortly (it retries on its own).
      if (response.status === 202) return { status: 'pending' };
      const body = await json(response);
      const deletedAt =
        typeof body === 'object' && body !== null && 'deletedAt' in body ? body.deletedAt : null;
      return { status: 'deleted', deletedAt: typeof deletedAt === 'string' ? deletedAt : null };
    },
  };
}
